import {
  labelFor,
  ANAESTHETIC_PRIORITY_OPTIONS,
  ANAESTHETIC_SETTING_OPTIONS,
  CASE_TYPE_OPTIONS,
  careLevelLabel,
  COMMUNICATION_CATEGORY_OPTIONS,
  COMMUNICATION_OUTCOME_OPTIONS,
  EDT_ACTIVITY_OPTIONS,
  formatDuration,
  procedureLabel,
} from '@iculogbook/shared';
import type {
  LocalAnaestheticCase,
  LocalAnyEntry,
  LocalCase,
  LocalCommunication,
  LocalEdtEntry,
  LocalProcedure,
} from '@iculogbook/shared';

import { cx } from '../../lib/cx.ts';
import type { DayGroup, EntryItem, EntryKind, SyncState } from './model.ts';
import { syncStateOf } from './model.ts';

const TIME = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
/** The end of an EDT entry's period. Day and month is enough on a row. */
const SPAN_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const DAY = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

export type RowActions = {
  onOpen: (entry: LocalAnyEntry, kind: EntryKind) => void;
  onDuplicate: (entry: LocalAnyEntry, kind: EntryKind) => void;
  onDelete: (entry: LocalAnyEntry, kind: EntryKind) => void;
};

/**
 * One day of entries, under its date.
 *
 * A case renders as one block with its procedures indented beneath it, because
 * that is what happened: one admission, and the things done during it. Four
 * sibling rows would say something different and less true.
 */
export function DayGroupRows({
  group,
  failedIds,
  actions,
}: {
  group: DayGroup;
  failedIds: ReadonlySet<string>;
  actions: RowActions;
}) {
  const count = group.items.reduce(
    (total, item) => total + (item.kind === 'case' ? 1 + item.procedures.length : 1),
    0,
  );

  return (
    <section>
      {/*
        Deliberately not sticky, and deliberately transparent.

        Sticky was the only thing that ever required this to be opaque — rows
        scroll underneath a stuck header and would otherwise read through the
        date. An opaque band is exactly what stops the paper showing through,
        and the two ways to have both are a `background-attachment: fixed`
        grid, which iOS Safari renders as `scroll` and so slides while the
        sheet holds still, or a per-element scroll offset in JavaScript, on
        every header of a virtualised list.

        Neither is worth it for a group that is a handful of rows tall and
        whose date is almost always already on screen. Transparent and static
        wins: the sheet shows through untouched, on every browser.
      */}
      <h2 className="flex items-baseline justify-between gap-3 py-2">
        {/* The serif marks the structure of the list. Nothing else on this
            screen is Newsreader — every row and numeral stays in Inter so the
            times column keeps its tabular alignment. */}
        <span className="font-serif text-base font-medium tracking-tight text-text">
          {DAY.format(group.date)}
        </span>
        <span className="text-xs text-faint">
          {count === 1 ? '1 entry' : `${String(count)} entries`}
        </span>
      </h2>

      {/*
        One card per day, with plain rows inside it. A card per row would double
        the vertical space of a screen people scroll through years of, and
        multiply the node count of a list that is virtualised precisely because
        that count matters. See H1 in docs/DESIGN_HANDOVER.md.
      */}
      <ul className="divide-y divide-line rounded-lg bg-surface shadow-sm">
        {group.items.map((item) => (
          <li key={item.record.id}>
            <EntryItemRow item={item} failedIds={failedIds} actions={actions} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function EntryItemRow({
  item,
  failedIds,
  actions,
}: {
  item: EntryItem;
  failedIds: ReadonlySet<string>;
  actions: RowActions;
}) {
  if (item.kind === 'procedure') {
    return (
      <ProcedureRow
        record={item.record}
        state={syncStateOf(item.record, failedIds)}
        actions={actions}
      />
    );
  }

  if (item.kind === 'edt') {
    return (
      <EdtRow record={item.record} state={syncStateOf(item.record, failedIds)} actions={actions} />
    );
  }

  if (item.kind === 'anaesthetic') {
    return (
      <AnaestheticRow
        record={item.record}
        state={syncStateOf(item.record, failedIds)}
        actions={actions}
      />
    );
  }

  if (item.kind === 'communication') {
    return (
      <CommunicationRow
        record={item.record}
        state={syncStateOf(item.record, failedIds)}
        actions={actions}
      />
    );
  }

  return (
    <div>
      <CaseRow record={item.record} state={syncStateOf(item.record, failedIds)} actions={actions} />

      {item.procedures.length === 0 ? null : (
        /*
         * Nesting is carried by a single hairline rule rather than by indent
         * plus a bullet: it costs less width, which matters on a phone, and it
         * survives a long procedure name without the text wrapping under its
         * own marker.
         */
        <ul className="ml-4 border-l border-line">
          {item.procedures.map((procedure) => (
            <li key={procedure.id}>
              <ProcedureRow
                record={procedure}
                state={syncStateOf(procedure, failedIds)}
                actions={actions}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CaseRow({
  record,
  state,
  actions,
}: {
  record: LocalCase;
  state: SyncState;
  actions: RowActions;
}) {
  return (
    <Row
      time={TIME.format(record.occurredAt)}
      icon="◆"
      iconLabel={labelFor(CASE_TYPE_OPTIONS, record.caseType)}
      title={record.title}
      meta={[
        labelFor(CASE_TYPE_OPTIONS, record.caseType),
        // `=== undefined`, not truthiness: level 0 belongs on the row.
        record.careLevel === undefined ? '' : careLevelLabel(record.careLevel),
      ]
        .filter((part) => part !== '')
        .join(' · ')}
      badge={levelBadge(record.capabilityLevel)}
      state={state}
      onOpen={() => actions.onOpen(record, 'case')}
      onDuplicate={() => actions.onDuplicate(record, 'case')}
      onDelete={() => actions.onDelete(record, 'case')}
    />
  );
}

function ProcedureRow({
  record,
  state,
  actions,
}: {
  record: LocalProcedure;
  state: SyncState;
  actions: RowActions;
}) {
  const meta = [
    record.variant,
    record.side === undefined
      ? undefined
      : `${record.side[0]?.toUpperCase()}${record.side.slice(1)}`,
    record.ultrasoundGuided === true ? 'US' : undefined,
    record.successful === false ? 'Unsuccessful' : undefined,
  ]
    .filter((part): part is string => part !== undefined)
    .join(' · ');

  return (
    <Row
      time={TIME.format(record.occurredAt)}
      icon="▸"
      iconLabel="Procedure"
      title={procedureLabel(record.procedureKey)}
      meta={meta}
      badge={levelBadge(record.capabilityLevel)}
      state={state}
      onOpen={() => actions.onOpen(record, 'procedure')}
      onDuplicate={() => actions.onDuplicate(record, 'procedure')}
      onDelete={() => actions.onDelete(record, 'procedure')}
    />
  );
}

/**
 * An EDT entry. No capability level and no age band, because there is no
 * patient — the badge slot carries the time spent instead, where one was
 * recorded, which is the number an ARCP panel reads these for.
 */
function EdtRow({
  record,
  state,
  actions,
}: {
  record: LocalEdtEntry;
  state: SyncState;
  actions: RowActions;
}) {
  return (
    <Row
      time={TIME.format(record.occurredAt)}
      icon="✎"
      iconLabel="EDT"
      title={record.title}
      meta={[
        labelFor(EDT_ACTIVITY_OPTIONS, record.activity),
        // A period, when the work was spread over one. The row's own time
        // column carries the start.
        record.endsAt === undefined ? '' : `to ${SPAN_DATE.format(record.endsAt)}`,
      ]
        .filter((part) => part !== '')
        .join(' · ')}
      badge={
        record.durationMinutes === undefined
          ? undefined
          : {
              text: formatDuration(record.durationMinutes),
              srLabel: `Time spent ${formatDuration(record.durationMinutes)}`,
            }
      }
      state={state}
      onOpen={() => actions.onOpen(record, 'edt')}
      onDuplicate={() => actions.onDuplicate(record, 'edt')}
      onDelete={() => actions.onDelete(record, 'edt')}
    />
  );
}

/**
 * A Communication entry: a significant discussion, most often withdrawal or
 * withholding of treatment. Carries a capability level, like a Case or a
 * Procedure — there is a patient — but no title, so the category itself is
 * the row's title, the same as an EDT row falls back to its activity.
 */
function CommunicationRow({
  record,
  state,
  actions,
}: {
  record: LocalCommunication;
  state: SyncState;
  actions: RowActions;
}) {
  const meta = [
    (record.withWhom ?? []).length === 0 ? undefined : `${String((record.withWhom ?? []).length)} present`,
    record.outcome === undefined ? undefined : labelFor(COMMUNICATION_OUTCOME_OPTIONS, record.outcome),
  ]
    .filter((part): part is string => part !== undefined)
    .join(' · ');

  return (
    <Row
      time={TIME.format(record.occurredAt)}
      icon="✦"
      iconLabel="Communication"
      title={labelFor(COMMUNICATION_CATEGORY_OPTIONS, record.category)}
      meta={meta}
      badge={levelBadge(record.capabilityLevel)}
      state={state}
      onOpen={() => actions.onOpen(record, 'communication')}
      onDuplicate={() => actions.onDuplicate(record, 'communication')}
      onDelete={() => actions.onDelete(record, 'communication')}
    />
  );
}

/**
 * An anaesthetic case. A hollow diamond against the Case's filled one: the same
 * kind of thing — one patient, one episode — but not ICM, which is exactly the
 * distinction the whole record type exists to hold.
 *
 * No Duplicate in its menu. These are imported from the RCoA platform and there
 * is no form that creates one, so a duplicate would have nowhere to go.
 */
function AnaestheticRow({
  record,
  state,
  actions,
}: {
  record: LocalAnaestheticCase;
  state: SyncState;
  actions: RowActions;
}) {
  const meta = [
    labelFor(ANAESTHETIC_SETTING_OPTIONS, record.setting),
    record.priority === undefined
      ? undefined
      : labelFor(ANAESTHETIC_PRIORITY_OPTIONS, record.priority),
    record.asa === undefined ? undefined : `ASA ${String(record.asa)}`,
  ]
    .filter((part): part is string => part !== undefined)
    .join(' · ');

  return (
    <Row
      time={TIME.format(record.occurredAt)}
      icon="◇"
      iconLabel="Anaesthetic"
      title={record.title}
      meta={meta}
      badge={levelBadge(record.capabilityLevel)}
      state={state}
      onOpen={() => actions.onOpen(record, 'anaesthetic')}
      onDelete={() => actions.onDelete(record, 'anaesthetic')}
    />
  );
}

/** The `L3` pill every clinical row carries. */
function levelBadge(level: number): { text: string; srLabel: string } {
  return { text: `L${String(level)}`, srLabel: `Capability level ${String(level)}` };
}

/*
 * Shape as well as colour, matching the header pill: a filled circle when the
 * row is backed up, a diamond while it waits, a square when it is failing.
 * Colour is never the only signal that a trainee's work is or is not safe.
 */
const SYNC_STYLES: Record<SyncState, { className: string; label: string }> = {
  synced: { className: 'rounded-full bg-success', label: 'Backed up' },
  pending: { className: 'rotate-45 rounded-xs bg-warning', label: 'Waiting to back up' },
  failed: { className: 'rounded-xs bg-danger', label: 'Backup problem' },
};

function Row({
  time,
  icon,
  iconLabel,
  title,
  meta,
  badge,
  state,
  onOpen,
  onDuplicate,
  onDelete,
  className,
}: {
  time: string;
  icon: string;
  iconLabel: string;
  title: string;
  meta: string;
  /** The pill on the right: a capability level, or a duration, or nothing. */
  badge?: { text: string; srLabel: string } | undefined;
  state: SyncState;
  onOpen: () => void;
  onDuplicate?: (() => void) | undefined;
  onDelete: () => void;
  className?: string;
}) {
  const sync = SYNC_STYLES[state];

  return (
    <div className={cx('flex items-center gap-1 pr-1', className)}>
      <button
        type="button"
        onClick={onOpen}
        // `min-w-0` matters: without it a long procedure label refuses to
        // shrink and pushes the actions menu off the edge of the screen.
        className="flex min-h-touch min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-surface-raised"
      >
        {/* Inter's tabular figures hold this column, so it no longer needs the
            mono stack to line up — and it now matches the rest of the row. */}
        <span className="w-10 shrink-0 text-xs tabular-nums text-faint">{time}</span>

        <span aria-hidden="true" className="shrink-0 text-xs text-faint">
          {icon}
        </span>
        <span className="sr-only">{iconLabel}: </span>

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-base text-text">{title}</span>
          {meta === '' ? null : <span className="truncate text-xs text-faint">{meta}</span>}
        </span>

        {badge === undefined ? null : (
          <span className="shrink-0 rounded-sm bg-surface-sunken px-1.5 py-0.5 text-xs text-muted">
            <span aria-hidden="true">{badge.text}</span>
            <span className="sr-only">{badge.srLabel}</span>
          </span>
        )}

        <span
          aria-hidden="true"
          title={sync.label}
          className={cx('size-2 shrink-0', sync.className)}
        />
        <span className="sr-only">{sync.label}</span>
      </button>

      <RowMenu onDuplicate={onDuplicate} onDelete={onDelete} onOpen={onOpen} title={title} />
    </div>
  );
}

/**
 * Row actions as a native `<details>` popover. No focus trapping to get wrong,
 * no library, and Escape closes it because the browser says so.
 */
function RowMenu({
  onOpen,
  onDuplicate,
  onDelete,
  title,
}: {
  onOpen: () => void;
  onDuplicate?: (() => void) | undefined;
  onDelete: () => void;
  title: string;
}) {
  return (
    <details className="relative shrink-0">
      <summary
        aria-label={`Actions for ${title}`}
        className="grid size-touch cursor-pointer list-none place-items-center rounded-md text-muted hover:bg-surface-raised hover:text-text [&::-webkit-details-marker]:hidden"
      >
        <span aria-hidden="true">⋯</span>
      </summary>

      <div className="absolute right-0 z-20 mt-1 flex w-40 flex-col rounded-md border border-line bg-surface-raised p-1 shadow-lg">
        <MenuItem onClick={onOpen}>Edit</MenuItem>
        {onDuplicate === undefined ? null : (
          <MenuItem onClick={onDuplicate}>Duplicate</MenuItem>
        )}
        <MenuItem onClick={onDelete} danger>
          Delete
        </MenuItem>
      </div>
    </details>
  );
}

function MenuItem({
  onClick,
  danger = false,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.currentTarget.closest('details')?.removeAttribute('open');
        onClick();
      }}
      className={cx(
        'min-h-touch rounded-sm px-3 text-left text-sm hover:bg-surface',
        danger ? 'text-danger' : 'text-text',
      )}
    >
      {children}
    </button>
  );
}
